import CCColors from '@/Utils/CCColors';
import React, {useState} from 'react';
import Dropzone from 'react-dropzone';
import {StyleSheet, View, ScrollView, TextInput, Alert} from 'react-native';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {useIntutionTrainerUpload} from '@/Hooks/BooksApi/useBooksApi';
import upload from '../../../../Assets/Images/png/upload.png';
import fileIcon from '../../../../Assets/Images/png/file.png';
import deleteIcon from '../../../../Assets/Images/png/delete.png';
import CCText from '../../../../Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import CCButton from '../../../../Atoms/CCButton';
import fontWeightToFamily from '@/Assets/Fonts/fontWeightToFamily';

const UploadPuzzle: React.FC<{buttonName: string; onClose: () => void}> = ({
  buttonName,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [startingMoveCount, setStartingMoveCount] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const {mutate, isLoading} = useIntutionTrainerUpload(
    () => {
      setMessage({type: 'success', text: 'Puzzle uploaded successfully!'});
      setFile(null);
      setStartingMoveCount('');
      setTimeout(onClose, 1000);
    },
    error => {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to upload the puzzle.',
      });
    },
  );

  const handleDrop = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
    setMessage(null);
  };

  const handleUpload = () => {
    if (!file) {
      setMessage({type: 'error', text: 'Please select a file to upload.'});
      return;
    }

    mutate({
      file,
      start_move: startingMoveCount
        ? parseInt(startingMoveCount, 10)
        : undefined,
    });
  };

  return (
    <View style={styles.container}>
      <ScrollView>
        {!file ? (
          <Dropzone
            accept={{'application/x-chess-pgn': ['.pgn']}}
            maxFiles={1}
            onDrop={handleDrop}>
            {({getRootProps, getInputProps}) => (
              <div {...getRootProps()} style={styles.dropContainer}>
                <input {...getInputProps()} />
                <img src={upload} alt="Upload Icon" />
                <Spacer spacing={vs(12)} />
                <CCText textAlign="center">
                  Drag and drop file {'\n'}or click here to select file
                </CCText>
              </div>
            )}
          </Dropzone>
        ) : (
          <>
            <View style={styles.dropContainer}>
              <img src={fileIcon} style={{width: 100}} alt="File Icon" />
              <Spacer spacing={vs(12)} />
              <CCText textAlign="center" style={{width: '80%'}}>
                {file.name}
              </CCText>
              <img
                src={deleteIcon}
                style={styles.deleteIcon}
                alt="Delete Icon"
                onClick={() => setFile(null)}
              />
            </View>
            <Spacer spacing={vs(20)} />
            <CCText>Starting Move Count (Optional)</CCText>
            <Spacer spacing={vs(5)} />
            <TextInput
              style={styles.input}
              placeholder="Enter starting move count"
              onChangeText={setStartingMoveCount}
              value={startingMoveCount}
              keyboardType="numeric"
              placeholderTextColor={CCColors.TextColor.Grey}
            />
            <Spacer spacing={vs(20)} />

            {/* Success/Error Message */}
            {message && (
              <CCText
                style={{
                  color: message.type === 'success' ? 'green' : 'red',
                  textAlign: 'center',
                }}>
                {message.text}
              </CCText>
            )}
            <Spacer spacing={vs(10)} />

            <View style={styles.button}>
              <CCButton
                icon={upload}
                stretched
                titleStyle={{color: CCColors.TextColor.Dark, padding: 15}}
                onPress={handleUpload}
                loading={isLoading}>
                {buttonName ? buttonName : 'Upload File'}
              </CCButton>
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    flexDirection: 'column',
    flex: 1,
  },
  title: {
    fontSize: FontSizes[24],
    fontFamily: fontWeightToFamily('medium'),
  },
  input: {
    height: 48,
    borderRadius: 8,
    padding: 12,
    border: `1px solid ${CCColors.Border}`,
  },
  button: {
    width: 'calc(100% - 120px)',
    alignSelf: 'center',
  },
  dropContainer: {
    borderRadius: 8,
    border: '1px dashed rgba(34, 34, 34, 0.20)',
    backgroundColor: CCColors.Primary.lightBrown,
    height: 150,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  deleteIcon: {
    position: 'absolute',
    right: 10,
    top: 10,
    width: 30,
  },
});

export default UploadPuzzle;
